import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { 
  Sparkles,
  Link2,
  ChevronRight,
  ChevronDown,
  Check,
  Loader2,
  Globe,
  Facebook,
  Instagram,
  User,
  Building2,
  Upload,
  Phone,
  Mail,
  Globe2,
  Award,
  Camera,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Pinterest icon component
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

// Inline Social URL Input Component
function SocialUrlInputs({ onConnect }) {
  const [urls, setUrls] = useState({
    facebook: "",
    instagram: "",
    tiktok: "",
    pinterest: ""
  });
  const [connecting, setConnecting] = useState(false);
  
  const hasAtLeastOneUrl = Object.values(urls).some(url => url.trim().length > 0);
  
  const handleConnect = async () => {
    if (!hasAtLeastOneUrl) return;
    
    setConnecting(true);
    try {
      // Filter out empty URLs
      const filledUrls = Object.fromEntries(
        Object.entries(urls).filter(([_, url]) => url.trim().length > 0)
      );
      await onConnect(filledUrls);
    } finally {
      setConnecting(false);
    }
  };
  
  const platforms = [
    { key: "facebook", icon: Facebook, color: "blue-500", placeholder: "https://facebook.com/yourpage" },
    { key: "instagram", icon: Instagram, color: "pink-500", placeholder: "https://instagram.com/yourhandle" },
    { key: "tiktok", icon: TikTokIcon, color: "white", placeholder: "https://tiktok.com/@yourhandle" },
    { key: "pinterest", icon: PinterestIcon, color: "red-500", placeholder: "https://pinterest.com/yourprofile" }
  ];
  
  return (
    <div className="space-y-3">
      {platforms.map(({ key, icon: Icon, color, placeholder }) => (
        <div key={key} className="flex items-center gap-2">
          <div className={`p-2 rounded-lg bg-${color}/10 flex-shrink-0`}>
            <Icon className={`w-4 h-4 text-${color}`} />
          </div>
          <Input
            placeholder={placeholder}
            value={urls[key]}
            onChange={(e) => setUrls({ ...urls, [key]: e.target.value })}
            className="input-base flex-1 text-sm"
            data-testid={`social-url-${key}`}
          />
        </div>
      ))}
      
      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-viraly-muted">
          {hasAtLeastOneUrl 
            ? `✓ ${Object.values(urls).filter(u => u.trim()).length} platform(s) ready` 
            : "Enter at least one social media URL"}
        </p>
        <Button
          onClick={handleConnect}
          disabled={!hasAtLeastOneUrl || connecting}
          className="btn-primary"
          data-testid="connect-urls-btn"
        >
          {connecting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              Connect {hasAtLeastOneUrl ? `(${Object.values(urls).filter(u => u.trim()).length})` : ""} 
              <ChevronRight className="w-4 h-4 ml-1" />
            </>
          )}
        </Button>
      </div>
      
      <p className="text-xs text-yellow-400">
        ⚠ You must connect at least one account to continue
      </p>
    </div>
  );
}

export default function SetupBanner() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Setup steps
  const [currentStep, setCurrentStep] = useState(1);
  const [brandUrl, setBrandUrl] = useState("");
  const [learningBrand, setLearningBrand] = useState(false);
  const [brandLearned, setBrandLearned] = useState(false);
  const [brandType, setBrandType] = useState(null); // 'personal' or 'company'
  const [headshot, setHeadshot] = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [extractedImage, setExtractedImage] = useState(null); // Image extracted from URL
  const [showImageConfirm, setShowImageConfirm] = useState(false); // Show "Is this your photo?" prompt
  const [contactInfo, setContactInfo] = useState({
    phone: "",
    email: "",
    website: "",
    tagline: "",
    credentials: ""
  });

  // Check existing setup status
  const hasBrandSetup = user?.brand_voice || user?.brand_url;
  const hasBrandType = user?.brand_type;
  const hasHeadshot = user?.headshot_url;
  const hasFacebook = user?.social_connections?.facebook?.connected;
  const hasInstagram = user?.social_connections?.instagram?.connected;
  const hasTikTok = user?.social_connections?.tiktok?.connected;
  const hasPinterest = user?.social_connections?.pinterest?.connected;
  const hasAnySocialConnected = hasFacebook || hasInstagram || hasTikTok || hasPinterest;  // ONE is enough
  
  // Setup is complete when: Brand learned + Brand type selected + At least ONE social connected (MANDATORY)
  const setupComplete = hasBrandSetup && hasBrandType && hasAnySocialConnected;

  // Initialize state from user data
  useEffect(() => {
    if (hasBrandSetup) {
      setBrandLearned(true);
      setCurrentStep(2);
    }
    if (hasBrandType) {
      setBrandType(user.brand_type);
      setCurrentStep(3);
    }
    if (user?.contact_info) {
      setContactInfo(user.contact_info);
    }
    if (hasHeadshot) {
      setHeadshotPreview(user.headshot_url);
    }
    if (hasAnySocialConnected && hasBrandSetup && hasBrandType) {
      setCurrentStep(4); // Complete
    }
  }, [user]);

  // Don't show if setup complete
  if (setupComplete) {
    return null;
  }

  const handleLearnBrand = async () => {
    if (!brandUrl) {
      toast.error("Please enter your website or social media URL");
      return;
    }
    
    setLearningBrand(true);
    try {
      const res = await apiClient.post("/brand/learn-from-url", { url: brandUrl });
      
      if (res.data.brand_voice) {
        setBrandLearned(true);
        await refreshUser();
        
        // Silently check if we extracted an image - no error if not found
        if (res.data.extracted_image) {
          setExtractedImage(res.data.extracted_image);
          setShowImageConfirm(true);
          toast.success("Brand learned! We found a profile picture.", {
            description: "Is this you? Confirm or upload your own."
          });
        } else {
          // No image found - just proceed without mentioning it
          toast.success("Brand learned successfully!");
        }
        
        setCurrentStep(2);
      } else {
        toast.error("Could not analyze brand from URL. Please try a different URL.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze URL. Please try again.");
    } finally {
      setLearningBrand(false);
    }
  };
  
  // Accept extracted image as headshot
  const handleAcceptExtractedImage = async () => {
    if (!extractedImage) return;
    
    try {
      // Download the image and upload it as headshot
      const response = await fetch(extractedImage);
      const blob = await response.blob();
      const file = new File([blob], "profile-from-url.jpg", { type: blob.type });
      
      const formData = new FormData();
      formData.append("file", file);
      
      await apiClient.post("/users/headshot", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      await refreshUser();
      setShowImageConfirm(false);
      setHeadshotPreview(extractedImage);
      toast.success("Profile picture saved!");
    } catch (error) {
      toast.error("Could not save that image. Please upload manually.");
      setShowImageConfirm(false);
    }
  };
  
  // Reject extracted image - show upload option
  const handleRejectExtractedImage = () => {
    setShowImageConfirm(false);
    setExtractedImage(null);
    // No message needed - user will see upload option
  };

  const handleBrandTypeSelect = async (type) => {
    setBrandType(type);
    try {
      await apiClient.put("/users/profile", { brand_type: type });
      await refreshUser();
      setCurrentStep(3);
      
      if (type === 'personal') {
        toast.info("Great! Upload your headshot to feature in your posts.");
      } else {
        toast.success("Perfect! Now connect your social accounts.");
      }
    } catch (error) {
      toast.error("Failed to save. Please try again.");
    }
  };

  const handleHeadshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setHeadshotPreview(e.target.result);
    reader.readAsDataURL(file);
    setHeadshot(file);
    
    // Upload
    setUploadingHeadshot(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await apiClient.post("/users/headshot", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      await refreshUser();
      toast.success("Headshot uploaded! You'll appear in your branded posts.");
    } catch (error) {
      toast.error("Failed to upload. Please try again.");
      setHeadshotPreview(null);
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const handleSaveContactInfo = async () => {
    try {
      await apiClient.put("/users/profile", { contact_info: contactInfo });
      await refreshUser();
      toast.success("Contact info saved!");
      setShowContactInfo(false);
    } catch (error) {
      toast.error("Failed to save contact info");
    }
  };

  const handleSkipSocial = async () => {
    try {
      await apiClient.put("/users/profile", { skipped_social_setup: true });
      await refreshUser();
      toast.success("You can still create content! Connect socials anytime from Settings to enable auto-posting.", {
        duration: 5000
      });
    } catch (error) {
      toast.error("Failed to save preference");
    }
  };

  // Skip entire setup - mark as skipped but allow returning later
  const handleSkip = async () => {
    try {
      // Set both skipped_social_setup AND a general setup_skipped flag
      await apiClient.put("/users/profile", { 
        skipped_social_setup: true,
        setup_skipped: true  // General skip flag to completely hide banner
      });
      await refreshUser();
      toast.info("Setup skipped. You can complete it anytime from Settings.", {
        duration: 3000
      });
    } catch (error) {
      // Silently fail
    }
  };

  const getProgressPercent = () => {
    let progress = 0;
    if (brandLearned) progress += 35;
    if (brandType) progress += 25;
    if (hasAnySocialConnected) progress += 40;
    return progress;
  };

  return (
    <AnimatePresence>
      {/* Full-screen modal overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-viraly-dark/95 backdrop-blur-md overflow-y-auto"
        data-testid="setup-banner"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-xl bg-viraly-card border border-viraly-orange/30 rounded-2xl p-6 my-8 shadow-2xl shadow-viraly-orange/10"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-viraly-orange" />
              Complete Your Setup
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-viraly-orange font-medium">{getProgressPercent()}% Complete</span>
              {/* No skip button - setup is mandatory */}
            </div>
          </div>
          
          <p className="text-sm text-viraly-muted mb-6">
            Complete all 3 steps to unlock content creation. You need a brand identity and at least one social account.
          </p>

      {/* Progress Bar */}
      <div className="w-full h-2 bg-white/10 rounded-full mb-6 overflow-hidden">
        <motion.div 
          className="h-full bg-viraly-gradient"
          initial={{ width: 0 }}
          animate={{ width: `${getProgressPercent()}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Step 1: Learn Brand (MANDATORY) */}
      <div className={`p-4 rounded-xl mb-4 ${brandLearned ? 'bg-viraly-success/10 border border-viraly-success/30' : 'bg-white/5 border border-white/10'}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${brandLearned ? 'bg-viraly-success' : 'bg-viraly-orange'}`}>
            {brandLearned ? <Check className="w-5 h-5 text-white" /> : <span className="text-white font-bold">1</span>}
          </div>
          <div>
            <h4 className="font-medium">Learn Your Brand</h4>
            <p className="text-xs text-viraly-muted">Enter your website or social media URL</p>
          </div>
        </div>
        
        {!brandLearned ? (
          <div className="ml-11">
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Any URL - website, Facebook, Instagram, LinkedIn..."
                value={brandUrl}
                onChange={(e) => setBrandUrl(e.target.value)}
                className="input-base flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleLearnBrand()}
                data-testid="brand-url-input"
              />
              <Button
                onClick={handleLearnBrand}
                disabled={learningBrand || !brandUrl}
                className="btn-primary"
                data-testid="learn-brand-btn"
              >
                {learningBrand ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Learning...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Learn Brand
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-viraly-muted">
              We'll extract your logo, colors, and brand voice automatically
            </p>
          </div>
        ) : (
          <div className="ml-11 flex items-center justify-between">
            <span className="text-sm text-viraly-success">Brand learned from your URL</span>
            <Button
              onClick={() => navigate("/settings?tab=brand")}
              variant="ghost"
              size="sm"
              className="text-viraly-muted hover:text-white"
            >
              Review in Settings <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* Step 2: Personal or Company (MANDATORY) */}
      <div className={`p-4 rounded-xl mb-4 ${!brandLearned ? 'opacity-50 pointer-events-none' : ''} ${brandType ? 'bg-viraly-success/10 border border-viraly-success/30' : 'bg-white/5 border border-white/10'}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${brandType ? 'bg-viraly-success' : brandLearned ? 'bg-viraly-orange' : 'bg-white/20'}`}>
            {brandType ? <Check className="w-5 h-5 text-white" /> : <span className="text-white font-bold">2</span>}
          </div>
          <div>
            <h4 className="font-medium">Are you the face of your brand?</h4>
            <p className="text-xs text-viraly-muted">This helps us personalize your content images</p>
          </div>
        </div>
        
        {!brandType ? (
          <div className="ml-11 grid grid-cols-2 gap-3">
            <button
              onClick={() => handleBrandTypeSelect('personal')}
              className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-viraly-orange/50 hover:bg-white/10 transition-all text-left"
            >
              <User className="w-8 h-8 text-viraly-orange mb-2" />
              <p className="font-medium">Yes, Personal Brand</p>
              <p className="text-xs text-viraly-muted">I feature in my content</p>
            </button>
            <button
              onClick={() => handleBrandTypeSelect('company')}
              className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-blue-500/50 hover:bg-white/10 transition-all text-left"
            >
              <Building2 className="w-8 h-8 text-blue-400 mb-2" />
              <p className="font-medium">No, Company/Product</p>
              <p className="text-xs text-viraly-muted">Logo-based branding</p>
            </button>
          </div>
        ) : (
          <div className="ml-11">
            <div className="flex items-center justify-between">
              <span className="text-sm text-viraly-success flex items-center gap-2">
                {brandType === 'personal' ? <User className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                {brandType === 'personal' ? 'Personal Brand' : 'Company/Product Brand'}
              </span>
            </div>
            
            {/* Headshot Upload for Personal Brand */}
            {brandType === 'personal' && (
              <div className="mt-4 p-3 rounded-lg bg-white/5">
                {/* Extracted Image Confirmation */}
                {showImageConfirm && extractedImage && (
                  <div className="mb-4 p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30">
                    <p className="text-sm font-medium mb-3">Is this your profile picture?</p>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-viraly-orange">
                        <img src={extractedImage} alt="Extracted" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={handleAcceptExtractedImage}
                          className="bg-viraly-success hover:bg-viraly-success/80"
                        >
                          <Check className="w-4 h-4 mr-1" /> Yes, use this
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={handleRejectExtractedImage}
                        >
                          No, I'll upload
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Manual Upload Option */}
                {!showImageConfirm && (
                  <div className="flex items-center gap-4">
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-16 h-16 rounded-full bg-white/10 border-2 border-dashed border-white/20 hover:border-viraly-orange/50 flex items-center justify-center cursor-pointer overflow-hidden flex-shrink-0"
                    >
                      {headshotPreview || user?.headshot_url ? (
                        <img 
                          src={headshotPreview || (user?.headshot_url?.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${user.headshot_url}` 
                            : user?.headshot_url
                          )} 
                          alt="Profile" 
                          className="w-full h-full object-cover object-center" 
                        />
                      ) : uploadingHeadshot ? (
                        <Loader2 className="w-6 h-6 animate-spin text-viraly-muted" />
                      ) : (
                        <Camera className="w-6 h-6 text-viraly-muted" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {headshotPreview || user?.headshot_url ? 'Profile picture saved!' : 'Upload your profile picture / headshot'}
                      </p>
                      <p className="text-xs text-viraly-muted">This will appear on your branded posts</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleHeadshotUpload}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Optional Contact Info */}
            <div className="mt-3">
              <button
                onClick={() => setShowContactInfo(!showContactInfo)}
                className="text-sm text-viraly-muted hover:text-white flex items-center gap-1"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showContactInfo ? 'rotate-180' : ''}`} />
                {showContactInfo ? 'Hide' : 'Add'} contact info (optional)
              </button>
              
              <AnimatePresence>
                {showContactInfo && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 p-3 rounded-lg bg-white/5 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-viraly-muted mb-1 block">Phone</label>
                          <Input
                            placeholder="+1 234 567 8900"
                            value={contactInfo.phone}
                            onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                            className="input-base text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-viraly-muted mb-1 block">Email</label>
                          <Input
                            placeholder="you@company.com"
                            value={contactInfo.email}
                            onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                            className="input-base text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-viraly-muted mb-1 block">Tagline</label>
                        <Input
                          placeholder="Your motto or tagline"
                          value={contactInfo.tagline}
                          onChange={(e) => setContactInfo({...contactInfo, tagline: e.target.value})}
                          className="input-base text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-viraly-muted mb-1 block">Credentials</label>
                        <Input
                          placeholder="License #, certifications, etc."
                          value={contactInfo.credentials}
                          onChange={(e) => setContactInfo({...contactInfo, credentials: e.target.value})}
                          className="input-base text-sm"
                        />
                      </div>
                      <Button onClick={handleSaveContactInfo} size="sm" className="btn-secondary">
                        Save Contact Info
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      {/* Step 3: Connect Social Accounts (MANDATORY - at least one required) */}
      <div className={`p-4 rounded-xl ${!brandType ? 'opacity-50 pointer-events-none' : ''} ${hasAnySocialConnected ? 'bg-viraly-success/10 border border-viraly-success/30' : 'bg-white/5 border border-white/10'}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasAnySocialConnected ? 'bg-viraly-success' : brandType ? 'bg-viraly-orange' : 'bg-white/20'}`}>
            {hasAnySocialConnected ? <Check className="w-5 h-5 text-white" /> : <span className="text-white font-bold">3</span>}
          </div>
          <div className="flex-1">
            <h4 className="font-medium">Connect Social Accounts <span className="text-yellow-400 text-xs">(Required)</span></h4>
            <p className="text-xs text-viraly-muted">Enter your social media profile URLs (at least one required)</p>
          </div>
        </div>
        
        <div className="ml-11">
          {hasAnySocialConnected ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-lg ${hasFacebook ? 'bg-blue-500/20 ring-2 ring-blue-500' : 'bg-white/5'}`}>
                  <Facebook className={`w-5 h-5 ${hasFacebook ? 'text-blue-500' : 'text-viraly-muted'}`} />
                </div>
                <div className={`p-2 rounded-lg ${hasInstagram ? 'bg-pink-500/20 ring-2 ring-pink-500' : 'bg-white/5'}`}>
                  <Instagram className={`w-5 h-5 ${hasInstagram ? 'text-pink-500' : 'text-viraly-muted'}`} />
                </div>
                <div className={`p-2 rounded-lg ${hasTikTok ? 'bg-white/20 ring-2 ring-white' : 'bg-white/5'}`}>
                  <TikTokIcon className={`w-5 h-5 ${hasTikTok ? 'text-white' : 'text-viraly-muted'}`} />
                </div>
                <div className={`p-2 rounded-lg ${hasPinterest ? 'bg-red-500/20 ring-2 ring-red-500' : 'bg-white/5'}`}>
                  <svg className={`w-5 h-5 ${hasPinterest ? 'text-red-500' : 'text-viraly-muted'}`} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
                  </svg>
                </div>
                
                <Button
                  onClick={() => navigate("/settings?tab=social")}
                  className="btn-secondary"
                  size="sm"
                  data-testid="manage-social-btn"
                >
                  Manage Accounts
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <p className="text-xs text-viraly-success">
                ✓ {[hasFacebook && 'Facebook', hasInstagram && 'Instagram', hasTikTok && 'TikTok', hasPinterest && 'Pinterest'].filter(Boolean).join(', ')} connected
              </p>
            </>
          ) : (
            <SocialUrlInputs 
              onConnect={async (urls) => {
                try {
                  // Save all entered URLs at once
                  const response = await apiClient.post("/users/social-urls", { urls });
                  if (response.data.success) {
                    await refreshUser();
                    toast.success("Social accounts connected!");
                  }
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to save social URLs");
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Completion Message */}
      {setupComplete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-4 p-4 rounded-xl bg-viraly-success/20 border border-viraly-success/30 text-center"
        >
          <Check className="w-8 h-8 text-viraly-success mx-auto mb-2" />
          <p className="font-medium text-viraly-success">Setup Complete!</p>
          <p className="text-sm text-viraly-muted">You're ready to create personalized content</p>
        </motion.div>
      )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
